import { useState, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Trans, useTranslation } from 'react-i18next';
import { Building, BuildingListing, StoreState } from '@root/types';
import { compact } from 'lodash';
import { useBreakpoint } from '@shared/useBreakpoints';
import { PARAMETERS } from '@root/tracking/constants';
import routes from '@root/routes';
import cn from 'classnames';
import {
  AddToSavedItemsButton,
  OnlyInDesktopTablet,
  Notification,
  ErrorBoundary,
} from '@components/shared';
import Location from '@components/shared/Location';
import Transportation from '@components/shared/Transportation';
import Divider from '@components/shared/Divider';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import Bartender from '@components/shared/Bartender';
import { Tooltip } from 'antd';
import useContactViewChooser from '@components/shared/ContactList/useContactViewChooser';
import { getSessionStorageValues } from '@root/shared/sessionStorageForSiteVisitors';
import Amenities from '@components/shared/Amenity/Amenities';
import { useSelector } from 'react-redux';
import useAnalytics from '@shared/useAnalytics';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api';
import analyticsActions from '@store/actions/saved/analytics';
import i18next from 'i18next';
import Certifications from './Certifications';
import VideoTour from '../Listing/VideoTour';
import Title from './Title';
import BasicInfo from './BasicInfo';
import s from './Building.module.less';
import StaticMapImage from './StaticMapImage';
import AvailableSpaces, { ListingTab } from './AvailableSpaces';
import Images from './Images';
import Header from './Header/Header';

type Props = {
  building: Building;
  listingClicked: (listing: BuildingListing, tab: ListingTab | '') => void;
  hasCurrentUser: boolean;
  isPreview: boolean;
  isSaved: boolean;
};

const videoSourceContent = {
  three_d_tour: PARAMETERS.spotlightTour,
  video: PARAMETERS.buildingVideo,
};

const Layout = ({ building, listingClicked, hasCurrentUser, isPreview, isSaved }: Props) => {
  const [hasRendered, setHasRendered] = useState(false);
  useEffect(() => setHasRendered(true), []);
  const { clickToPage, saveInteraction } = useAnalytics();

  const queryClient = useQueryClient();

  const { isMobile } = useBreakpoint();
  const flags = useFlags();
  const { t } = useTranslation('building');
  const currentUser = useSelector((state: StoreState) => state.currentUser);

  const { arrivedOnMarketLink, arrivedOnShareLink, sessionStorageListingSharer } =
    getSessionStorageValues();

  const listingSharerForDisplay = sessionStorageListingSharer ? [sessionStorageListingSharer] : [];

  const {
    showContactList,
    showLinkSharerContactInfo,
    ContactList,
    LinkSharerContactInfo,
    makeContactEmailSubject,
  } = useContactViewChooser({ arrivedOnMarketLink, arrivedOnShareLink });

  const contactEmailSubject: string = makeContactEmailSubject({
    address: compact([building.address.buildingName, building.address.street]).join(' '),
    userName: currentUser?.firstName,
  });

  const locationSection = useRef<HTMLDivElement | null>(null);
  const handleScrollToLocation = () => {
    if (locationSection.current) {
      locationSection.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const isLoggedOutUserFromShareLink =
    !hasCurrentUser && (arrivedOnMarketLink || arrivedOnShareLink);
  const showBreadcrumbLink = hasCurrentUser || !isLoggedOutUserFromShareLink;
  const toggleSaveBuildingMutation = useMutation(
    async (shouldBeSaved: boolean) => {
      if (shouldBeSaved) {
        await api.post(routes.api.buildingSave(building.slug));
      } else {
        await api.delete(routes.api.buildingSave(building.slug));
      }
    },
    {
      onSuccess: async (_, wasSaved) => {
        saveInteraction({
          action: wasSaved ? PARAMETERS.saveItem : PARAMETERS.unsaveItem,
          sourcePage: PARAMETERS.buildingPage,
        });
        if (wasSaved) {
          Notification.saveSuccess({
            title: i18next.t('building:saved'),
            /* eslint-disable react/jsx-no-literals */
            text: (
              <Trans i18nKey="viewInFavorites" ns="building">
                View it in your
                <a
                  href="/saved/buildings"
                  onClick={analyticsActions.clickToSavedBuildingsFromToast}
                >
                  saved buildings
                </a>
                .
              </Trans>
            ),
            /* eslint-enable react/jsx-no-literals */
          });
        }
        await queryClient.invalidateQueries([routes.api.saveBuilding()]);
      },
    },
  );

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const availableSpacesRef = useRef<HTMLDivElement | null>(null);
  const handleScroll = () => {
    if (availableSpacesRef.current) {
      availableSpacesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const addToSavedItemsProps = {
    isSaved,
    onClick: async (shouldBeSaved: boolean) => {
      toggleSaveBuildingMutation.mutate(shouldBeSaved);
    },
    className: s.saveButton,
    'data-testid': 'save-building-button',
  };

  return (
    <>
      <OnlyInDesktopTablet>
        {!!building.marketName && (
          <Breadcrumbs
            items={[
              {
                content: building.marketName,
                link: showBreadcrumbLink ? routes.search(building.marketSlug!) : undefined,
                onClick: () =>
                  clickToPage({
                    sourcePage: PARAMETERS.buildingPage,
                    sourceContent: PARAMETERS.breadcrumb,
                    destination: PARAMETERS.searchResultsPage,
                    actionType: 'BUILDING_TO_BREADCRUMB_CLICKED',
                  }),
              },
              {
                content: building.address.buildingName || building.address.street,
              },
            ]}
          />
        )}
      </OnlyInDesktopTablet>
      <Bartender
        drinks={[
          {
            url: routes.admin.building(building.id),
            tooltipText: 'Admin Page of the Building',
            icon: 'buildings-multiple',
          },
        ]}
        className={s.bartender}
      />
      <ErrorBoundary>
        {flags['outreach.branding-building'] ? (
          <Header
            address={building.address}
            neighborhood={building.neighborhood}
            bannerImage={building?.bannerImage}
            lettermarkLogo={building?.lettermarkLogo}
            buildingSlug={building.slug}
            isSaved={isSaved}
            owningCompany={building.owningCompany}
            onClickAvailability={handleScroll}
          />
        ) : (
          <div className={s.titleContainer}>
            <Title address={building.address} neighborhood={building.neighborhood} />
            {hasCurrentUser && (
              <div className={s.saveButtonContainer}>
                {isMobile ? (
                  <AddToSavedItemsButton {...addToSavedItemsProps} />
                ) : (
                  <Tooltip trigger={['hover']} placement="bottom" title={t('saveTooltip')}>
                    <span>
                      <AddToSavedItemsButton {...addToSavedItemsProps} />
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        )}

        {hasRendered && (
          <Images
            address={building.address}
            firstPhotoOrientation={building.firstPhotoOrientation}
            photos={building.photos}
            video={building.video}
            tourEmbedUrl={building.tourEmbedUrl}
            sourcePage={PARAMETERS.buildingPage}
            sourceContent={PARAMETERS.buildingPageGallery}
            imagesZipUrl={routes.api.buildingImagesZipUrl(building.slug)}
            onLightboxOpened={() => setIsLightboxOpen(true)}
            onLightboxClosed={() => setIsLightboxOpen(false)}
          />
        )}
      </ErrorBoundary>
      <div className="grid grid-cols-3 gap-2 tablet:grid-cols-4 mobile:grid-cols-1">
        <ErrorBoundary>
          <div className="col-start-1 col-end-3 tablet:col-end-4">
            <BasicInfo building={building} />
            {showContactList && (
              <ContactList
                contacts={building.listingAgents}
                title={t('tourThisBuilding')}
                emailSubjectName={contactEmailSubject}
                removeBottomBorder
              />
            )}
          </div>
          <div className="col-start-3 tablet:col-start-4 mobile:hidden">
            <StaticMapImage
              onScrollToLocation={handleScrollToLocation}
              transitOptions={building.transitOptions}
              className={s.mapImage}
              buildingAddress={building.locationAddress}
            />
            {showLinkSharerContactInfo && (
              <LinkSharerContactInfo
                contacts={listingSharerForDisplay}
                emailSubjectName={contactEmailSubject}
                title={t('tourThisBuilding')}
                sourcePage={PARAMETERS.buildingPage}
              />
            )}
          </div>
        </ErrorBoundary>
      </div>
      <div ref={availableSpacesRef}>
        <ErrorBoundary>
          <AvailableSpaces
            listingClicked={listingClicked}
            building={building}
            isPreview={isPreview}
          />
        </ErrorBoundary>
      </div>
      <div className="my-10 grid grid-cols-3 gap-2 mobile:my-0 mobile:grid-cols-1">
        <ErrorBoundary>
          <div className="col-start-1 col-end-3 mobile:col-span-full">
            <div className="mb-10 mobile:mb-8 mobile:pt-1">
              <Amenities amenities={building.amenities} />
            </div>
            <Certifications
              leedCertification={building.leedCertification}
              wiredCertification={building.wiredCertification}
            />
          </div>
        </ErrorBoundary>
      </div>
      <ErrorBoundary>
        <Divider className={s.divider} />
        <VideoTour
          video={building.video}
          sourcePage={PARAMETERS.buildingPage}
          sourceContent={
            flags['media-analytics']
              ? PARAMETERS.spotlightTour
              : videoSourceContent[building.virtualTourSpotlight]
          }
          tourEmbedUrl={building.tourEmbedUrl}
          subtitle={false}
          virtualTourSpotlight={building.virtualTourSpotlight}
          isVisible={!isLightboxOpen}
        />
        <Divider className={cn(s.divider, s.withExtraPadding)} />
      </ErrorBoundary>
      <div ref={locationSection}>
        <ErrorBoundary>
          <Location address={building.locationAddress} />
          <Transportation transitOptions={building.transitOptions} />
        </ErrorBoundary>
      </div>
    </>
  );
};

export default Layout;
